<template>
  <v-card flat>
    <v-card-actions class="d-print-none flex-wrap">
      <v-menu
        v-model="dateMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            class="week-field"
            :value="date"
            label="Week (Sunday)"
            prepend-icon="mdi-calendar"
            readonly
            hide-details
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="date"
          no-title
          scrollable
          :allowed-dates="isSunday"
          @change="dateMenu = false"
        />
      </v-menu>
      <v-spacer/>
      <v-select
        class="stream-select"
        label="Streams"
        v-model="streamFilter"
        :items="streamItems"
        multiple
        hide-details
        data-cy="cms-stream-filter"
      >
        <template v-slot:prepend-item>
          <v-list-item ripple @click="toggleAllStreams">
            <v-list-item-action>
              <v-icon>{{ allStreamsSelected ? 'mdi-close-box' : 'mdi-checkbox-blank-outline' }}</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ allStreamsSelected ? 'Select none' : 'Select all' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="mt-2"/>
        </template>
        <template v-slot:selection="{ item, index }">
          <span v-if="index === 0">{{ streamSelectionSummary }}</span>
        </template>
      </v-select>
      <v-spacer/>
      <v-select
        class="size-select"
        label="Nutrients for size"
        v-model="size"
        :items="sizeItems"
        hide-details
      />
      <v-spacer/>
      <v-btn outlined :disabled="rows.length === 0" @click="downloadCsv" data-cy="cms-download">
        <v-icon left small>mdi-download</v-icon>
        Download CSV
      </v-btn>
    </v-card-actions>

    <v-card-text class="d-print-none pb-0">
      <v-row>
        <v-col cols="12" md="6">
          <h4 class="mb-1">TOD column</h4>
          <p class="text-caption mb-2">
            Rename times of day in the export. The first rule with a meal's time of day renames it; one no
            rule has is left as it is.
          </p>
          <div
            v-for="(rule, index) of todRules"
            :key="index"
            class="d-flex align-center mb-2"
            data-cy="cms-tod-rule"
          >
            <v-select
              label="times of day"
              :value="rule.tods"
              @change="updateTodRule(index, {tods: $event})"
              :items="todItems"
              multiple
              small-chips
              dense
              hide-details
              data-cy="cms-tod-rule-tods"
            />
            <v-icon class="mx-2">mdi-arrow-right</v-icon>
            <v-text-field
              label="renamed"
              :value="rule.label"
              @change="updateTodRule(index, {label: $event})"
              dense
              hide-details
              class="rename-field"
              data-cy="cms-tod-rule-label"
            />
            <v-btn icon small @click="removeTodRule(index)" title="Remove rule" data-cy="cms-tod-rule-remove">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
          <v-btn text small @click="addTodRule" data-cy="cms-tod-rule-add">
            <v-icon left small>mdi-plus</v-icon>
            Add rule
          </v-btn>
        </v-col>
        <v-col cols="12" md="6">
          <h4 class="mb-1">Misc Tags</h4>
          <p class="text-caption mb-2">
            A meal with none of a restriction's tags gets that restriction's tag. Every meal also gets its
            ingredient tags, e.g. "contains garlic", when they are included.
          </p>
          <v-autocomplete
            label="Restrictions to tag"
            :value="restrictionTagIds"
            @change="setRestrictionTagIds"
            :items="restrictionItems"
            multiple
            chips
            small-chips
            deletable-chips
            dense
            hide-details
            data-cy="cms-restrictions"
          />
          <v-row dense class="mt-2">
            <v-col cols="12" sm="6" v-for="restriction of selectedRestrictions" :key="restriction.id">
              <v-text-field
                :label="`tag for '${restriction.text}'`"
                :value="restrictionLabels[restriction.id]"
                @change="setRestrictionLabel(restriction.id, $event)"
                :placeholder="restriction.text"
                persistent-placeholder
                dense
                hide-details
              />
            </v-col>
          </v-row>
          <v-checkbox
            v-model="includeIngredientTags"
            label="Include ingredient tags"
            hide-details
            dense
          />
        </v-col>
      </v-row>
    </v-card-text>

    <v-alert v-if="error" type="error" outlined class="mx-4">{{ error }}</v-alert>

    <v-card-text>
      <v-data-table
        :loading="loading"
        :headers="headers"
        :items="rows"
        item-key="Meal ID"
        :items-per-page="-1"
        hide-default-footer
        dense
        no-data-text="No meals in the chosen streams for this week"
      >
        <template v-slot:[imageSlot]="{ value }">
          <a v-if="value" :href="value" target="_blank">
            <img :src="value" class="meal-thumb" alt="meal"/>
          </a>
        </template>
        <template v-slot:[nutritionSlot]="{ value }">
          <a :href="value" target="_blank">{{ value }}</a>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import moment from 'moment';
import api from '@/api';
import urlState from '@/router/urlState';
import {csvExport, getNextOrderingDate} from '@/store/utils';
import {mealParam} from 'fed-lib/src/meal-url';

// chef's choice meals sit in every stream's 'any' day; the export treats them as a stream of their own
export const CHEFS_CHOICE_STREAM = 'chefs_choice';

const SIZE_LETTERS = {small: 's', medium: 'm', large: 'l'};

// column name -> nutrient code in the menu's meal.nutrients[size], and the unit written after the value
const NUTRIENT_COLUMNS = [
  ['Protein', 'PROT', 'g'],
  ['Net Carbs', 'NETCARB', 'g'],
  ['Iron', 'FE', 'mg'],
  ['Calcium', 'CA', 'mg'],
  ['Fibre', 'TDF', 'g'],
  ['Potassium', 'K', 'mg'],
  ['Folate', 'FOLA', 'µg'],
];

const TOD_ORDER = ['breakfast', 'lunch', 'dinner', 'extras'];

// the cms has one entree slot and one sides slot; a url with no tod entries uses these rules
const DEFAULT_TOD_RULES = [
  {tods: ['lunch', 'dinner', 'single serve entree'], label: 'entree'},
  {tods: ['protein side', 'sides'], label: 'sides'},
];
// the url's tod entry for a page with every rule removed, so a reload does not bring the default back
const NO_TOD_RULES = 'none';

function byTodOrder(a, b) {
  const rank = tod => (TOD_ORDER.includes(tod) ? TOD_ORDER.indexOf(tod) : TOD_ORDER.length);
  return rank(a) - rank(b) || a.localeCompare(b);
}

/** rules match times of day case-insensitively, so they hold them lowercase; a rename keeps its case */
function todKey(tod) {
  return `${tod || ''}`.trim().toLowerCase();
}

/** a rule the export can apply: some times of day and a name for them */
function isCompleteRule(rule) {
  return rule.tods.length > 0 && !!rule.label;
}

/** 'lunch,dinner:entree' */
function todRuleToQuery(rule) {
  return `${rule.tods.join(',')}:${rule.label}`;
}

function todRuleFromQuery(entry) {
  const [tods, ...label] = `${entry}`.split(':');
  return {tods: [...new Set(tods.split(',').map(todKey).filter(Boolean))], label: label.join(':')};
}

// food tag names that run their words together, as they should read in a tag
const TAG_WORDS = {
  bellpeppers: 'bell peppers',
  bokchoy: 'bok choy',
  brownpoultry: 'brown poultry',
  brownrice: 'brown rice',
  brusselssprouts: 'brussels sprouts',
  chickenthigh: 'chicken thigh',
  codfish: 'cod fish',
  goatcheese: 'goat cheese',
  greenpepper: 'green pepper',
  hempseeds: 'hemp seeds',
  oliveoil: 'olive oil',
  poppyseeds: 'poppy seeds',
  proteinpowder: 'protein powder',
  pumpkinseeds: 'pumpkin seeds',
  ranchdressing: 'ranch dressing',
  sundriedtomato: 'sun-dried tomato',
  sunflowerseeds: 'sunflower seeds',
  treenuts: 'tree nuts',
  whitefish: 'white fish',
};

/** a food tag as a cms tag: 'has-garlic' -> 'contains garlic', 'is-spicy' -> 'spicy', 'nut-free' -> 'nut free' */
export function formatIngredientTag(tag) {
  const name = `${tag || ''}`.trim().toLowerCase();
  const words = text => TAG_WORDS[text] || text.replace(/-/g, ' ');
  if (name.startsWith('has-')) {
    return `contains ${words(name.slice('has-'.length))}`;
  }
  if (name.startsWith('is-')) {
    return words(name.slice('is-'.length));
  }
  return words(name);
}

/** a meal suits a restriction when it has none of the food tags the restriction rules out */
export function mealSuitsRestriction(meal, restriction) {
  const mealTagIds = new Set(Object.keys(meal.food_tags || {}).map(Number));
  return !restriction.restrictedTags.some(tagId => mealTagIds.has(Number(tagId)));
}

/**
 * A list of 'key:value' query entries as an object; the value runs to the end, so it may hold a colon.
 * A bare 'key' has an empty value.
 */
function parsePairs(entries) {
  return Object.fromEntries((Array.isArray(entries) ? entries : [entries])
    .filter(Boolean)
    .map((entry) => {
      const [key, ...value] = `${entry}`.split(':');
      return [key, value.join(':')];
    }));
}

/** a chef's choice menu id is '10191CC'; the meal id is the number */
function mealIdOf(menuId) {
  return `${menuId}`.replace(/CC$/i, '');
}

/**
 * a url-safe name with the meal's code on the end (fed-lib meal-url.js): the
 * same string as the web app's meal page URL, /meal/<slug>, so a CMS item
 * links straight to it; the code stands for the id, so two meals with the
 * same name never share a slug and a renamed meal keeps its code
 */
export function mealSlug(meal) {
  return mealParam(meal.id, meal.name);
}

export function nutritionPageUrl(mealId, size) {
  return `https://fed.fyi/#/chart/fedfedfed/${mealId}/${SIZE_LETTERS[size]}`;
}

/** '18g': the value to one decimal place with its unit, or blank when the menu has no value */
function nutrientWithUnit(value, unit) {
  return typeof value === 'number' ? `${Math.round(value * 10) / 10}${unit}` : '';
}

/**
 * Every meal on a week's menu, once each, with the streams offering it. The menu is keyed by
 * stream (regular and virtual alike); a virtual stream carries copies of regular stream meals
 * under its own id. Only the given streams are read.
 */
export function mealsByStream(menu, streamIds) {
  const wanted = new Set(streamIds);
  const byMealId = new Map();
  const add = (meal, streamId) => {
    const mealId = mealIdOf(meal.id);
    const found = byMealId.get(mealId) || {meal, streams: new Set(), tods: new Set()};
    found.streams.add(streamId);
    if (meal.tod) {
      found.tods.add(meal.tod);
    }
    byMealId.set(mealId, found);
  };
  Object.entries(menu || {}).forEach(([streamId, stream]) => {
    ((stream && stream.menu) || []).forEach((day) => {
      if (day.date === 'any') {
        if (wanted.has(CHEFS_CHOICE_STREAM)) {
          (day.products || []).forEach(meal => add(meal, CHEFS_CHOICE_STREAM));
        }
      } else if (wanted.has(streamId)) {
        (day.meals || []).forEach(meal => add(meal, streamId));
      }
    });
  });
  return [...byMealId.values()];
}

export default {
  name: 'MenuCmsExport',
  mixins: [urlState],
  data() {
    return {
      todRules: DEFAULT_TOD_RULES.map(rule => ({...rule})),
      restrictions: [],
      restrictionTagIds: [],
      restrictionLabels: {},
      includeIngredientTags: false,
      date: getNextOrderingDate(),
      dateMenu: false,
      menu: null,
      streamFilter: [],
      size: 'medium',
      sizeItems: Object.keys(SIZE_LETTERS),
      loading: false,
      error: null,
      // the column names have spaces, which a slot name in the template can't
      imageSlot: 'item.Meal Image',
      nutritionSlot: 'item.Nutrition Page URL',
    };
  },
  mounted() {
    this.syncToUrl({param: 'date', urlParam: 'date', initFromRoute: true});
    this.syncToUrl({
      param: 'streamFilter', urlParam: 'streams', initFromRoute: true,
      parseCallback: v => (Array.isArray(v) ? v : [v]).filter(Boolean),
    });
    this.syncToUrl({param: 'size', urlParam: 'size', initFromRoute: true});
    // the advanced options: ?tod=lunch,dinner:entree&tod=breakfast:brunch&restrict=garlic:garlic%20free&ingredientTags=true
    this.syncToUrl({param: 'todQuery', urlParam: 'tod', initFromRoute: true});
    this.syncToUrl({param: 'restrictionQuery', urlParam: 'restrict', initFromRoute: true});
    this.syncToUrl({
      param: 'includeIngredientTags', urlParam: 'ingredientTags', initFromRoute: true,
      transformCallback: v => (v ? 'true' : null),
      parseCallback: v => v === 'true',
    });
    this.fetchStreams();
    return Promise.all([this.fetchMenu(), this.fetchRestrictions()]);
  },
  watch: {
    date: 'fetchMenu',
  },
  computed: {
    ...mapGetters(['getStreamName']),
    /** the streams on this week's menu, regular ones first, then virtual ones, then chef's choice */
    streamItems() {
      const entries = Object.entries(this.menu || {});
      const items = entries
        .filter(([, stream]) => stream && (stream.menu || []).some(day => day.date !== 'any'))
        .map(([id, stream]) => ({
          value: id,
          text: `${this.getStreamName(id) || id}${stream.derived ? ' (virtual)' : ''}`,
          isVirtual: !!stream.derived,
        }))
        .sort((a, b) => a.isVirtual - b.isVirtual || a.text.localeCompare(b.text));
      const hasChefsChoice = entries.some(([, stream]) => ((stream && stream.menu) || [])
        .some(day => day.date === 'any' && (day.products || []).length > 0));
      if (hasChefsChoice) {
        items.push({value: CHEFS_CHOICE_STREAM, text: "Chef's Choice"});
      }
      return items;
    },
    allStreamsSelected() {
      return this.streamItems.length > 0 && this.streamItems.every(item => this.streamFilter.includes(item.value));
    },
    streamSelectionSummary() {
      if (this.allStreamsSelected) {
        return 'All streams';
      }
      const selected = this.streamItems.filter(item => this.streamFilter.includes(item.value));
      return selected.length <= 2
        ? selected.map(item => item.text).join(', ')
        : `${selected.length} of ${this.streamItems.length} streams`;
    },
    /**
     * The tod rules in the url, one 'lunch,dinner:entree' entry per complete rule: nothing while
     * they are the default, and 'none' once every rule is removed. A rule still being filled in
     * stays on the page only.
     */
    todQuery: {
      get() {
        const rules = this.todRules.filter(isCompleteRule).map(todRuleToQuery);
        if (JSON.stringify(rules) === JSON.stringify(DEFAULT_TOD_RULES.map(todRuleToQuery))) {
          return null;
        }
        return rules.length > 0 ? rules : [NO_TOD_RULES];
      },
      set(entries) {
        const list = (Array.isArray(entries) ? entries : [entries]).filter(Boolean);
        if (list.length === 0) {
          this.todRules = DEFAULT_TOD_RULES.map(rule => ({...rule}));
          return;
        }
        this.todRules = list
          .filter(entry => entry !== NO_TOD_RULES)
          .map(todRuleFromQuery)
          .filter(isCompleteRule);
      },
    },
    /** the times of day on this week's menu, plus any a rule already names */
    todItems() {
      const tods = new Set(this.todRules.flatMap(rule => rule.tods));
      Object.values(this.menu || {}).forEach(stream => ((stream && stream.menu) || []).forEach((day) => {
        [...(day.meals || []), ...(day.products || [])].forEach((meal) => {
          if (meal.tod) {
            tods.add(todKey(meal.tod));
          }
        });
      }));
      return [...tods].sort(byTodOrder);
    },
    /** the restrictions to tag in the url, as 'id' or 'id:tag' when the tag is renamed */
    restrictionQuery: {
      get() {
        return this.restrictionTagIds.map((id) => {
          const label = this.restrictionLabels[id];
          return label ? `${id}:${label}` : id;
        });
      },
      set(entries) {
        const labels = parsePairs(entries);
        this.restrictionTagIds = Object.keys(labels);
        this.restrictionLabels = labels;
      },
    },
    /** only a restriction with food tags can be checked against a meal */
    restrictionItems() {
      return this.restrictions
        .filter(restriction => (restriction.restrictedTags || []).length > 0)
        .map(restriction => ({value: restriction.id, text: restriction.text || restriction.id}))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    selectedRestrictions() {
      return this.restrictionTagIds
        .map(id => this.restrictions.find(restriction => restriction.id === id))
        .filter(restriction => restriction && (restriction.restrictedTags || []).length > 0);
    },
    headers() {
      return [
        'Meal ID', 'Meal Name', 'Slug', 'Meal Image', 'Nutrition Page URL',
        ...NUTRIENT_COLUMNS.map(([column]) => column),
        'TOD', 'Streams', 'Misc Tags',
      ].map(column => ({text: column, value: column}));
    },
    /** one row per meal, keyed by the csv column names so the preview is exactly what downloads */
    rows() {
      return mealsByStream(this.menu, this.streamFilter)
        .map(({meal, streams, tods}) => {
          const mealId = mealIdOf(meal.id);
          const photos = meal.photos || {};
          const nutrients = (meal.nutrients && meal.nutrients[this.size]) || {};
          return {
            'Meal ID': mealId,
            'Meal Name': `${meal.name || ''}`.trim(),
            'Slug': mealSlug(meal),
            'Meal Image': photos.plated || photos.closeup || photos.packaged || '',
            'Nutrition Page URL': nutritionPageUrl(mealId, this.size),
            ...Object.fromEntries(NUTRIENT_COLUMNS.map(([column, code, unit]) => [column, nutrientWithUnit(nutrients[code], unit)])),
            'TOD': this.todLabels(tods).join(', '),
            'Streams': [...streams].map(streamId => this.streamLabel(streamId)).join(', '),
            'Misc Tags': this.miscTags(meal).join(', '),
          };
        })
        .sort((a, b) => a['Meal Name'].localeCompare(b['Meal Name']) || a['Meal ID'] - b['Meal ID']);
    },
  },
  methods: {
    ...mapActions(['fetchStreams']),
    isSunday(d) {
      return moment(d).day() === 0;
    },
    streamLabel(streamId) {
      if (streamId === CHEFS_CHOICE_STREAM) {
        return "Chef's Choice";
      }
      return this.getStreamName(streamId) || streamId;
    },
    /** a meal's times of day, in day order, renamed by the tod rules - two renamed alike show once */
    todLabels(tods) {
      const rules = this.todRules.filter(isCompleteRule);
      const labels = [...tods]
        .sort(byTodOrder)
        .map((tod) => {
          const rule = rules.find(r => r.tods.includes(todKey(tod)));
          return rule ? rule.label : tod;
        });
      return [...new Set(labels)];
    },
    miscTags(meal) {
      const restrictionTags = this.selectedRestrictions
        .filter(restriction => mealSuitsRestriction(meal, restriction))
        .map(restriction => `${this.restrictionLabels[restriction.id] || ''}`.trim() || restriction.text);
      const ingredientTags = this.includeIngredientTags
        ? Object.values(meal.food_tags || {}).map(formatIngredientTag).sort()
        : [];
      return [...new Set([...restrictionTags, ...ingredientTags].filter(Boolean))];
    },
    updateTodRule(index, change) {
      const rule = {...this.todRules[index], ...change};
      rule.label = `${rule.label || ''}`.trim();
      rule.tods = [...new Set((rule.tods || []).map(todKey))].sort(byTodOrder);
      this.todRules = this.todRules.map((r, i) => (i === index ? rule : r));
    },
    addTodRule() {
      this.todRules = [...this.todRules, {tods: [], label: ''}];
    },
    removeTodRule(index) {
      this.todRules = this.todRules.filter((r, i) => i !== index);
    },
    setRestrictionTagIds(ids) {
      this.restrictionTagIds = ids;
    },
    setRestrictionLabel(id, value) {
      this.restrictionLabels = {...this.restrictionLabels, [id]: `${value || ''}`.trim()};
    },
    async fetchRestrictions() {
      try {
        const {data} = await api.get('substitutions/');
        this.restrictions = Object.values(data || {}).filter(restriction => restriction && restriction.id);
      } catch (e) {
        console.error('could not load restrictions', e);
        this.error = `Could not load restrictions: ${e.message || e}`;
      }
    },
    toggleAllStreams() {
      this.streamFilter = this.allStreamsSelected ? [] : this.streamItems.map(item => item.value);
    },
    async fetchMenu() {
      if (!this.date) {
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const {data} = await api.get(`v2/menu/${this.date}`);
        this.menu = data;
        if (!data) {
          this.error = 'No menu has been published for this week.';
        }
        // a fresh visit exports every stream until some are picked
        if (this.streamFilter.length === 0) {
          this.streamFilter = this.streamItems.map(item => item.value);
        }
      } catch (e) {
        console.error('menu cms export failed', e);
        this.menu = null;
        this.error = (e.response && e.response.data && e.response.data.error) || e.message || `${e}`;
      } finally {
        this.loading = false;
      }
    },
    downloadCsv() {
      csvExport(this.rows, `menu-cms-export-${this.date}.csv`);
    },
  },
};
</script>

<style scoped>
.week-field {
  max-width: 200px;
}

.stream-select {
  max-width: 320px;
}

.rename-field {
  max-width: 160px;
}

.size-select {
  max-width: 160px;
}

.meal-thumb {
  height: 40px;
  width: 40px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
}
</style>
